import React, { useState } from 'react';
import { MoreVertical, ChevronDown, Pencil, Trash2, CheckCircle2 } from 'lucide-react';
import { Toggle } from './Toggle';
import { useAddress } from '../hooks/useAddress';
import { useAuth } from '../hooks/useAuth';
import { useModal } from '../hooks/useModal';
import AddWarehousePage from '../pages/settings/warehouse/add';
import toast from 'react-hot-toast';
import { PickupAddress } from '../types/address';
import { cn } from '../utils/cn';

interface WarehouseRowProps {
  warehouse: PickupAddress;
  rate?: boolean;
  // Deprecated props for fallback if needed, but we'll update all callers
  id?: string | number;
  name?: string;
  primary?: boolean;
  address?: string;
  spoc?: string;
  phone?: string;
  active?: any;
}

export const WarehouseRow: React.FC<WarehouseRowProps> = ({
  warehouse,
  rate = false,
}) => {
  const { user } = useAuth();
  const { updateAddress, deleteAddress, deleteAddressAsync } = useAddress(
    user?.id as string
  );
  const { open, close, Modal } = useModal();

  const [expanded, setExpanded] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Sync with warehouse status (1: active, 0: inactive)
  const isEnabled = warehouse.status === 1;

  const handleToggle = async (checked: boolean) => {
    try {
      const newStatus = checked ? 1 : 0;
      await updateAddress({
        pickupId: warehouse.pickup_id as any,
        payload: {
          ...warehouse,
          status: newStatus,
        },
      });
      toast.success(`${checked ? 'activating' : 'deactivating'} warehouse`);
    } catch (err) {
      toast.error('Failed to update warehouse status');
    }
  };

  const handleDelete = () => {
    open({
      Component: () => (
        <div className="p-6">
          <p className="text-gray-600 mb-6 font-semibold">
            Are you sure you want to delete this warehouse address? This action
            cannot be undone.
          </p>
          <div className="flex justify-end gap-3 font-semibold">
            <button
              onClick={close}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                const toastId = toast.success('Deleting warehouse...');
                try {
                  await deleteAddressAsync(warehouse.pickup_id as any);
                  toast.success('Warehouse deleted successfully', {
                    id: toastId,
                  });
                  close();
                } catch (err: any) {
                  toast.error(err.message || 'Failed to delete warehouse', {
                    id: toastId,
                  });
                }
              }}
              className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
    });
  };

  const handleSetPrimary = async () => {
    try {
      const toastId = toast.success('Setting as primary address...');
      await updateAddress({
        pickupId: warehouse.pickup_id as any,
        payload: {
          ...warehouse,
          default_status: 1,
        },
      });
      toast.success('Primary address updated', { id: toastId });
      setShowDropdown(false);
    } catch (err) {
      toast.error('Failed to update primary address');
    }
  };

  const handleEdit = () => {
    open({
      Component: () => (
        <AddWarehousePage
          initialData={warehouse}
          onSuccess={() => {
            close();
          }}
        />
      ),
      centered: true,
      closable: true,
      className: 'max-w-4xl w-full h-[92vh]',
    });
  };

  const handleRowClick = () => {
    if (!rate) {
      setExpanded((prev) => !prev);
    }
  };

  const fullAddress = `${warehouse.address1}, ${warehouse.city}, ${warehouse.state}, ${warehouse.country}, ${warehouse.pincode}`;

  return (
    <div className="border-b last:border-b-0 hover:bg-gray-50 transition-colors">
      {/* MAIN ROW */}
      <div
        className={cn(
          'grid grid-cols-12 gap-10 px-10 py-6 items-center',
          rate ? 'cursor-default' : 'cursor-pointer'
        )}
        onClick={handleRowClick}
      >
        {/* Address Info */}
        <div className={rate ? 'col-span-10' : 'col-span-6'}>
          <div className="flex items-center gap-3 mb-1">
            <p className="font-semibold text-gray-900">
              {warehouse.company_name}
            </p>
            {warehouse.default_status === 1 && (
              <span className="rounded-full bg-green-50 border border-green-200 px-3 py-0.5 text-[10px] font-bold text-green-700 uppercase tracking-tight">
                Primary
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-2">{fullAddress}</p>
        </div>

        {!rate && <div className="col-span-2"></div>}

        {/* Status Toggle */}
        {!rate && (
          <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
            <Toggle checked={isEnabled} onChange={handleToggle} />
          </div>
        )}

        {/* Actions Dropdown */}
        {!rate && (
          <div
            className="col-span-2 flex justify-end items-center gap-3 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <ChevronDown
              className={cn(
                'h-5 w-5 transition-transform',
                expanded && 'rotate-180'
              )}
            />

            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/10 hover:bg-accent/20 transition-colors"
              >
                <MoreVertical className="h-5 w-5 text-accent" />
              </button>

              {showDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  />
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-muted rounded-xl shadow-xl z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {warehouse.default_status !== 1 && (
                      <button
                        onClick={handleSetPrimary}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-gray-700">
                          Set as Primary
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        handleEdit();
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Pencil className="w-4 h-4 text-accent" />
                      <span className="font-semibold">Edit Address</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        handleDelete();
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="font-semibold">Delete Address</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* EXPANDED CONTENT */}
      {!rate && expanded && (
        <div className="px-10 pb-6">
          <div className="rounded-[16px] bg-muted/20 p-8">
            <h3 className="mb-6 text-lg font-semibold text-gray-900">
              Contact Info
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Name</p>
                <p className="font-semibold">{warehouse.contact_name}</p>
              </div>

              <div>
                <p className="text-gray-500 mb-1">Phone</p>
                <p className="font-semibold">{warehouse.contact_no}</p>
              </div>

              <div>
                <p className="text-gray-500 mb-1">Email</p>
                <p className="font-semibold">{warehouse.email || 'N/A'}</p>
              </div>

              <div>
                <p className="text-gray-500 mb-1">Location Id</p>
                <p className="font-semibold">{warehouse.pickup_id}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <Modal />
    </div>
  );
};
